var _excluded = ["children", "as"],
  _excluded2 = ["rootProps"],
  _excluded3 = ["type"],
  _excluded4 = ["icon", "iconSpacing", "command", "commandSpacing", "children"],
  _excluded5 = ["icon", "iconSpacing"],
  _excluded6 = ["className", "title"],
  _excluded7 = ["title", "children", "className"],
  _excluded8 = ["className", "children"],
  _excluded9 = ["className"];
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { tqc, forwardRef, omitThemingProps, StylesProvider, useMultiStyleConfig, useStyles } from '../../system';
import { cx, runIfFn, __DEV__ } from '../../utils';
import * as React from 'react';
import { MenuProvider, useMenu, useMenuButton, useMenuContext, useMenuItem, useMenuList, useMenuOption, useMenuOptionGroup, useMenuPositioner } from './useMenu';
import { ScaleFade } from '../transition';
/**
 * Menu provides context, state, and focus management
 * to its sub-components. It doesn't render any DOM node.
 */
export var Menu = props => {
  var {
    children
  } = props;
  var styles = useMultiStyleConfig('Menu', props);
  var ownProps = omitThemingProps(props);
  var ctx = useMenu(ownProps);
  var context = React.useMemo(() => ctx, [ctx]);
  var {
    isOpen,
    onClose,
    forceUpdate
  } = context;
  return /*#__PURE__*/React.createElement(MenuProvider, {
    value: context
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, runIfFn(children, {
    isOpen,
    onClose,
    forceUpdate
  })));
};
if (__DEV__) {
  Menu.displayName = 'Menu';
}
var StyledMenuButton = forwardRef((props, ref) => {
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.button, _extends({
    ref: ref
  }, props, {
    __css: _objectSpread({
      display: 'inline-flex',
      appearance: 'none',
      alignItems: 'center',
      outline: 0,
      transition: 'all 250ms'
    }, styles.button)
  }));
});

/**
 * The trigger for the menu list. Must be a direct child of `Menu`.
 */
export var MenuButton = forwardRef((props, ref) => {
  var {
      children,
      as: As
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  var buttonProps = useMenuButton(rest, ref);
  var Element = As || StyledMenuButton;
  return /*#__PURE__*/React.createElement(Element, _extends({}, buttonProps, {
    className: cx('tqc-menu__menu-button', props.className)
  }), /*#__PURE__*/React.createElement(tqc.span, {
    __css: {
      pointerEvents: 'none',
      flex: '1 1 auto'
    }
  }, props.children));
});
if (__DEV__) {
  MenuButton.displayName = 'MenuButton';
}
export var MenuList = forwardRef((props, ref) => {
  var _props$zIndex, _styles$list;
  var {
      rootProps
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  var {
    isOpen
  } = useMenuContext();
  var listProps = useMenuList(rest, ref);
  var positionerProps = useMenuPositioner(rootProps);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.div, _extends({}, positionerProps, {
    __css: {
      zIndex: (_props$zIndex = props.zIndex) !== null && _props$zIndex !== void 0 ? _props$zIndex : (_styles$list = styles.list) === null || _styles$list === void 0 ? void 0 : _styles$list.zIndex
    }
  }), /*#__PURE__*/React.createElement(ScaleFade, _extends({}, listProps, {
    className: cx('tqc-menu__menu-list', listProps.className),
    in: isOpen,
    __css: _objectSpread({
      outline: 0
    }, styles.list)
  })));
});
if (__DEV__) {
  MenuList.displayName = 'MenuList';
}
var StyledMenuItem = forwardRef((props, ref) => {
  var {
      type
    } = props,
    rest = _objectWithoutProperties(props, _excluded3);
  var styles = useStyles();

  /**
   * Given another component, use its type if present
   * Else, use no type to avoid invalid html, e.g. <a type="button" />
   * Else, fall back to "button"
   */
  var btnType = rest.as ? type !== null && type !== void 0 ? type : undefined : 'button';
  var buttonStyles = _objectSpread({
    textDecoration: 'none',
    color: 'inherit',
    userSelect: 'none',
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    textAlign: 'start',
    flex: '0 0 auto',
    outline: 0
  }, styles.item);
  return /*#__PURE__*/React.createElement(tqc.button, _extends({
    ref: ref,
    type: btnType
  }, rest, {
    __css: buttonStyles
  }));
});
export var MenuItem = forwardRef((props, ref) => {
  var {
      icon,
      iconSpacing = '0.75rem',
      command,
      commandSpacing = '0.75rem',
      children
    } = props,
    rest = _objectWithoutProperties(props, _excluded4);
  var menuItemProps = useMenuItem(rest, ref);
  var shouldWrap = icon || command;
  var _children = shouldWrap ? /*#__PURE__*/React.createElement("span", {
    style: {
      pointerEvents: 'none',
      flex: 1
    }
  }, children) : children;
  return /*#__PURE__*/React.createElement(StyledMenuItem, _extends({}, menuItemProps, {
    className: cx('tqc-menu__menuitem', menuItemProps.className)
  }), icon && /*#__PURE__*/React.createElement(MenuIcon, {
    fontSize: "0.8em",
    marginRight: iconSpacing
  }, icon), _children, command && /*#__PURE__*/React.createElement(MenuCommand, {
    marginStart: commandSpacing
  }, command));
});
if (__DEV__) {
  MenuItem.displayName = 'MenuItem';
}
var CheckIcon = props => /*#__PURE__*/React.createElement("svg", _extends({
  viewBox: "0 0 14 14",
  width: "1em",
  height: "1em"
}, props), /*#__PURE__*/React.createElement("polygon", {
  fill: "currentColor",
  points: "5.5 11.9993304 14 3.49933039 12.5 2 5.5 8.99933039 1.5 4.9968652 0 6.49933039"
}));
export var MenuItemOption = forwardRef((props, ref) => {
  var {
      icon,
      iconSpacing = '0.75rem'
    } = props,
    rest = _objectWithoutProperties(props, _excluded5);
  var optionProps = useMenuOption(rest, ref);
  return /*#__PURE__*/React.createElement(StyledMenuItem, _extends({}, optionProps, {
    className: cx('tqc-menu__menuitem-option', rest.className)
  }), /*#__PURE__*/React.createElement(MenuIcon, {
    fontSize: "0.8em",
    marginRight: iconSpacing,
    opacity: props.isChecked ? 1 : 0
  }, icon || /*#__PURE__*/React.createElement(CheckIcon, null)), /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1
    }
  }, optionProps.children));
});
MenuItemOption.id = 'MenuItemOption';
if (__DEV__) {
  MenuItemOption.displayName = 'MenuItemOption';
}
export var MenuOptionGroup = props => {
  var {
      className,
      title
    } = props,
    rest = _objectWithoutProperties(props, _excluded6);
  var ownProps = useMenuOptionGroup(rest);
  return /*#__PURE__*/React.createElement(MenuGroup, _extends({
    title: title,
    className: cx('tqc-menu__option-group', className)
  }, ownProps));
};
if (__DEV__) {
  MenuOptionGroup.displayName = 'MenuOptionGroup';
}
export var MenuGroup = forwardRef((props, ref) => {
  var {
      title,
      children,
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded7);
  var _className = cx('tqc-menu__group__title', className);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: "tqc-menu__group",
    role: "group"
  }, title && /*#__PURE__*/React.createElement(tqc.p, _extends({
    className: _className
  }, rest, {
    __css: styles.groupTitle
  }), title), children);
});
if (__DEV__) {
  MenuGroup.displayName = 'MenuGroup';
}
export var MenuCommand = forwardRef((props, ref) => {
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.span, _extends({
    ref: ref
  }, props, {
    __css: styles.command,
    className: "tqc-menu__command"
  }));
});
if (__DEV__) {
  MenuCommand.displayName = 'MenuCommand';
}
export var MenuIcon = props => {
  var {
      className,
      children
    } = props,
    rest = _objectWithoutProperties(props, _excluded8);
  var child = React.Children.only(children);
  var clone = /*#__PURE__*/React.isValidElement(child) ? /*#__PURE__*/React.cloneElement(child, {
    focusable: 'false',
    'aria-hidden': true,
    className: cx('tqc-menu__icon', child.props.className)
  }) : null;
  var _className = cx('tqc-menu__icon-wrapper', className);
  return /*#__PURE__*/React.createElement(tqc.span, _extends({
    className: _className
  }, rest, {
    __css: {
      flexShrink: 0
    }
  }), clone);
};
if (__DEV__) {
  MenuIcon.displayName = 'MenuIcon';
}
export var MenuDivider = props => {
  var {
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded9);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.hr, _extends({
    role: "separator",
    "aria-orientation": "horizontal",
    className: cx('tqc-menu__divider', className)
  }, rest, {
    __css: styles.divider
  }));
};
if (__DEV__) {
  MenuDivider.displayName = 'MenuDivider';
}